'use client';

import {
  AlertCircle,
  Building2,
  Calculator,
  Calendar,
  CheckCircle,
  ChevronLeft,
  Clock,
  Coffee,
  FileText,
  Loader2,
  Monitor,
  Save,
  Send,
  User,
  Users,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { EmailPreviewModal } from '../../components/quotes/EmailPreviewModal';
import { MarkAsPaidModal } from '../../components/quotes/MarkAsPaidModal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { cn } from '../../lib/utils';
import { api } from '../../services/api';

// ============================================================
// COMPOSANT : DocumentViewer
// Affiche un PDF en blob via iframe avec gestion mémoire
// ============================================================
interface DocumentViewerProps {
  document: {
    url?: string;
    data?: string;
    type?: string;
    name?: string;
    documentKey?: string;
  };
  requiredDocuments?: Array<{ key: string; label: string }>;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({ document, requiredDocuments = [] }) => {
  const [objectUrl, setObjectUrl] = useState<string>('');
  const [error, setError] = useState<string>('');

  // Trouver le label du document demandé
  const getDocumentLabel = () => {
    if (!document.documentKey) return null;
    const required = requiredDocuments.find((rd) => rd.key === document.documentKey);
    return required?.label;
  };

  const documentLabel = getDocumentLabel();
  const displayName = documentLabel || document.name || 'Document';

  useEffect(() => {
    let localBlobUrl = '';

    const createObjectUrl = () => {
      try {
        const raw = document.url || document.data || '';
        if (!raw) {
          setError('Aucune donnée disponible');
          return;
        }

        // Cas 1 : URL HTTP directe — pas de conversion nécessaire
        if (raw.startsWith('http://') || raw.startsWith('https://')) {
          setObjectUrl(raw);
          return;
        }

        // Cas 2 : data URI base64 — convertir en Blob URL
        const dataUrl = raw.startsWith('data:')
          ? raw
          : `data:${document.type || 'application/pdf'};base64,${raw.replace(/\s/g, '')}`;

        const matches = dataUrl.match(/^data:([^;]+);base64,(.+)$/);
        if (!matches) {
          setError('Format de données invalide');
          return;
        }

        const mimeType = matches[1];
        const base64 = matches[2];

        const byteCharacters = atob(base64);
        const byteNumbers = Array.from(byteCharacters).map((c) => c.charCodeAt(0));
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mimeType });

        const url = URL.createObjectURL(blob);
        localBlobUrl = url;
        setObjectUrl(url);
      } catch (err) {
        console.error('[DocumentViewer] Error creating object URL:', err);
        setError('Erreur lors du chargement du document');
      }
    };

    createObjectUrl();

    return () => {
      if (localBlobUrl) {
        URL.revokeObjectURL(localBlobUrl);
      }
    };
  }, [document]);

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
        <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
        <p className="text-sm text-red-700">{error}</p>
      </div>
    );
  }

  if (!objectUrl) {
    return (
      <div className="bg-gray-100 rounded-lg p-8 text-center">
        <Loader2 className="w-8 h-8 text-gray-400 mx-auto mb-2" />
        <p className="text-sm text-gray-500">Chargement du document...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden shadow-sm">
      <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <span className="text-sm font-bold text-slate-800">{displayName}</span>
          {documentLabel && document.name && <span className="text-xs text-slate-500">Fichier : {document.name}</span>}
        </div>
        <a
          href={objectUrl}
          download={document.name || 'document.pdf'}
          className="text-sm text-[#00758d] hover:text-[#005a6d] font-semibold transition-colors"
        >
          Télécharger
        </a>
      </div>
      <iframe src={objectUrl} className="w-full h-[600px]" title={displayName} style={{ border: 'none' }} />
    </div>
  );
};

export function QuoteProcessPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [booking, setBooking] = useState<any>(null);
  const [editedDetails, setEditedDetails] = useState<any>({});
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showMarkAsPaidModal, setShowMarkAsPaidModal] = useState(false);

  // Charger les données du booking (devis)
  useEffect(() => {
    const fetchQuote = async () => {
      try {
        const { data } = await api.get(`/quotes/admin/${id}`);
        if (data.success) {
          setBooking(data.data);

          // Préremplissage des données éditables avec les infos initiales si nécessaires
          const sd = data.data.specificDetails || {};
          setEditedDetails({
            ...sd,
            participantCount: sd.participantCount || data.data.participantCount || '',
            daysCount: sd.daysCount || 1,
            urgentQuote: sd.urgentQuote !== undefined ? sd.urgentQuote : data.data.isUrgent,
            equipmentIds: Array.isArray(sd.equipmentIds) ? sd.equipmentIds : [],
          });
        }
      } catch (err) {
        console.error('Error fetching quote:', err);
        toast.error('Erreur lors du chargement du devis');
      } finally {
        setLoading(false);
      }
    };
    fetchQuote();
  }, [id]);

  const handleUpdateDetail = (key: string, value: any) => {
    setEditedDetails((prev: any) => ({ ...prev, [key]: value }));
  };

  const handleSave = async (isFinal = false) => {
    setSaving(true);
    try {
      // Endpoint à créer côté serveur
      const { data } = await api.post(`/quotes/admin/${id}/finalize`, {
        specificDetails: editedDetails,
        sendEmail: isFinal,
      });

      if (data.success) {
        toast.success(isFinal ? 'Devis envoyé avec succès !' : 'Modifications enregistrées');
        if (isFinal) {
          navigate('/quotes');
        }
        return true;
      }
      return false;
    } catch (err: any) {
      console.error('Error finalizing quote:', err);
      toast.error(err.response?.data?.error || "Erreur lors de l'enregistrement");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleMarkAsPaid = async (data: { method: string; date: string; proofDocumentUrl: string }) => {
    setSaving(true);
    try {
      const response = await api.patch(`/bookings/admin/${id}/status`, {
        status: 'PAID',
        paymentMethod: data.method,
        paymentDate: data.date,
        proofDocumentUrl: data.proofDocumentUrl,
      });
      if (response.data.success) {
        toast.success(`Devis marqué comme payé par ${data.method === 'VIREMENT' ? 'Virement' : 'Chèque'} !`);
        navigate('/invoices');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenPreview = async () => {
    // On sauvegarde d'abord les modifications pour que l'API PDF
    // lise les données à jour depuis la base de données
    const success = await handleSave(false);
    if (success) {
      setShowPreviewModal(true);
    }
  };

  if (loading)
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner message="Chargement du devis" submessage="Veuillez patienter..." size="md" />
      </div>
    );

  const getEventTypeName = (eventType: string) => {
    if (!eventType) return 'Non précisé';
    if (eventType === 'OTHER') return booking.specificDetails?.eventTypeOther || 'Autre';
    if (!booking.spaceType?.eventTypes) return eventType;
    const found = booking.spaceType.eventTypes.find((et: any) => et.value === eventType);
    return found ? found.label : eventType;
  };

  const getLayoutName = (layoutId: string) => {
    if (!layoutId) return 'Standard';
    if (layoutId === 'OTHER') return 'Autre disposition';
    if (!booking.spaceType?.roomLayouts) return layoutId;
    const found = booking.spaceType.roomLayouts.find((rl: any) => rl.id === layoutId);
    return found ? found.name : layoutId;
  };

  return (
    <div className="flex flex-col gap-6 max-w-[1200px] mx-auto w-full pb-20">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <button
            onClick={() => navigate('/quotes')}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors mb-2 text-sm font-medium"
          >
            <ChevronLeft className="w-4 h-4" />
            Retour aux devis
          </button>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Traitement du devis</h1>
            <span className="px-2.5 py-1 bg-primary/10 text-primary border border-primary/20 rounded-full text-xs font-bold uppercase tracking-wider">
              {booking.reference}
            </span>
            {booking.status === 'OVERDUE' && (
              <span className="px-2.5 py-1 bg-rose-100 text-rose-700 border border-rose-300 rounded-full text-xs font-bold uppercase tracking-wider">
                En retard
              </span>
            )}
            {booking.status === 'URGENT' && (
              <span className="px-2.5 py-1 bg-orange-100 text-orange-700 border border-orange-300 rounded-full text-xs font-bold uppercase tracking-wider">
                Urgent
              </span>
            )}
          </div>
          <p className="text-slate-500 mt-1">Modifiez les paramètres pour générer le devis définitif</p>
        </div>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <button
            onClick={() => handleSave(false)}
            disabled={saving || booking?.status === 'PAID'}
            className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all shadow-sm disabled:opacity-75 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 className="w-4 h-4" /> : <Save className="w-4 h-4" />}
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
          {booking?.status === 'PAID' && (
            <div className="flex-1 sm:flex-none px-4 py-2.5 bg-slate-100 border border-slate-200 rounded-xl">
              <div className="flex items-center gap-2 text-slate-600">
                <CheckCircle className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-bold">Devis Payé</span>
              </div>
            </div>
          )}
          <button
            onClick={handleOpenPreview}
            disabled={saving || booking?.status === 'PAID'}
            className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-primary text-white hover:bg-primary/90 px-5 py-2.5 rounded-xl font-bold text-sm transition-all shadow-md shadow-primary/20 disabled:opacity-75 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 className="w-4 h-4" /> : <Send className="w-4 h-4" />}
            {saving ? 'Chargement...' : booking?.status === 'QUOTED' ? 'Renvoyer le Devis' : 'Envoyer le Devis'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Colonne Gauche : Formulaire de modification */}
        <div className="lg:col-span-2 space-y-6">
          <section className="bg-slate-50 border border-slate-200 rounded-2xl shadow-sm overflow-hidden mb-6">
            <div className="px-6 py-4 border-b border-slate-200 bg-slate-100/50 flex items-center gap-2">
              <FileText className="w-4 h-4 text-slate-600" />
              <h3 className="font-bold text-slate-800">Demande Initiale du Client</h3>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-slate-500 block text-xs font-bold uppercase mb-1">Période souhaitée :</span>
                <div className="font-medium text-slate-800 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  {booking.startDate ? new Date(booking.startDate).toLocaleDateString('fr-FR') : 'Non précisé'}
                  {booking.endDate &&
                    booking.endDate !== booking.startDate &&
                    ` - ${new Date(booking.endDate).toLocaleDateString('fr-FR')}`}
                </div>
              </div>
              <div>
                <span className="text-slate-500 block text-xs font-bold uppercase mb-1">Horaires :</span>
                <div className="font-medium text-slate-800 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-slate-400" />
                  {booking.startTime || 'Non précisé'} - {booking.endTime || 'Non précisé'}
                </div>
              </div>
              <div>
                <span className="text-slate-500 block text-xs font-bold uppercase mb-1">Type d'événement :</span>
                <div className="font-medium text-slate-800">{getEventTypeName(booking.eventType)}</div>
              </div>
              <div>
                <span className="text-slate-500 block text-xs font-bold uppercase mb-1">
                  Mise en disposition (Disposition) :
                </span>
                <div className="font-medium text-slate-800">
                  {getLayoutName(booking.specificDetails?.roomLayoutId)}
                  {booking.specificDetails?.roomLayoutOther && ` - ${booking.specificDetails.roomLayoutOther}`}
                </div>
              </div>
              {booking.isUrgent && (
                <div className="md:col-span-2">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-rose-100 text-rose-700 font-bold text-xs uppercase">
                    <AlertCircle className="w-3.5 h-3.5" />
                    Demande signalée comme URGENTE par le client
                  </span>
                </div>
              )}
              {booking.documents && booking.documents.length > 0 && (
                <div className="md:col-span-2 mt-2 pt-4 border-t border-slate-200">
                  <span className="text-slate-500 block text-xs font-bold uppercase mb-3">Documents joints :</span>
                  <div className="space-y-4">
                    {booking.documents.map((doc: any) => (
                      <DocumentViewer
                        key={doc.id}
                        document={{
                          url: doc.fileUrl,
                          name: doc.fileName,
                          type: doc.mimeType,
                          documentKey: doc.documentKey,
                        }}
                        requiredDocuments={booking.spaceType?.requiredDocs || []}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>

          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-primary" />
                <h3 className="font-bold text-slate-800">Paramètres de Réservation</h3>
              </div>
              <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">
                Choix client (Lecture seule)
              </span>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">Type d'espace</label>
                <div className="px-3 py-2 bg-slate-50 border border-slate-100 rounded-lg text-slate-600 font-medium">
                  {booking.spaceType?.name}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">Nombre de participants</label>
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <div className="w-full pl-10 pr-3 py-2 bg-slate-50 border border-slate-100 rounded-xl text-slate-600 font-medium">
                    {editedDetails.participantCount || '-'} participant(s)
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">Nombre de jours</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <div className="w-full pl-10 pr-3 py-2 bg-slate-50 border border-slate-100 rounded-xl text-slate-600 font-medium">
                    {editedDetails.daysCount || '-'} jour(s)
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">Durée / Type</label>
                <div className="w-full px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl text-slate-600 font-medium">
                  {editedDetails.durationType === 'HOUR'
                    ? 'Heure(s)'
                    : editedDetails.durationType === 'HALF_DAY'
                      ? 'Demi-journée'
                      : editedDetails.durationType === 'DAY' || editedDetails.durationType === 'FULL_DAY'
                        ? 'Journée complète'
                        : editedDetails.durationType || 'Non précisé'}
                </div>
              </div>

              {/* Équipements */}
              <div className="md:col-span-2 space-y-3 pt-2">
                <label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <Monitor className="w-4 h-4 text-primary" />
                  Équipements inclus (Choisis par le client)
                </label>
                <div className="flex flex-wrap gap-2">
                  {editedDetails.equipmentIds && editedDetails.equipmentIds.length > 0 ? (
                    booking.spaceType?.equipments
                      ?.filter((eq: any) => editedDetails.equipmentIds.includes(eq.id))
                      .map((eq: any) => (
                        <div
                          key={eq.id}
                          className="flex items-center gap-2 px-3 py-2 bg-primary/5 text-primary border border-primary/20 rounded-lg text-sm font-medium"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-primary/50"></span>
                          {eq.name}
                        </div>
                      ))
                  ) : (
                    <span className="text-sm text-slate-400 italic">Aucun équipement supplémentaire ajouté.</span>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Section Restauration - Affichée uniquement si restauration demandée */}
          {(editedDetails.wantsCoffeeBreak ||
            editedDetails.wantsMealBreak ||
            editedDetails.hasMealBreak ||
            booking.specificDetails?.mealCatererType ||
            booking.specificDetails?.mealSingDetails ||
            booking.specificDetails?.mealExternalName) && (
            <section className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center gap-2">
                <Coffee className="w-4 h-4 text-primary" />
                <h3 className="font-bold text-slate-800">Restauration & Services</h3>
              </div>
              <div className="p-6 space-y-6">
                {/* Infos client sur la restauration */}
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 space-y-4">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                    Instructions & Besoins Client
                  </h4>
                  <div className="grid grid-cols-1 gap-4 text-sm">
                    {/* Choix Restauration */}
                    <div className="border-b border-slate-200 pb-3">
                      <span className="text-slate-500 block text-[10px] uppercase font-bold mb-1">
                        Type de service Restauration :
                      </span>
                      <span className="font-semibold text-slate-800 capitalize flex items-center gap-2">
                        {booking.specificDetails?.mealCatererType === 'SING'
                          ? 'Restauration Sing'
                          : booking.specificDetails?.mealCatererType === 'EXTERNAL'
                            ? 'Traiteur Externe'
                            : 'Aucun spécifique'}
                      </span>
                    </div>

                    {/* Détails spécifiques si Restauration Sing */}
                    {booking.specificDetails?.mealSingDetails && (
                      <div className="bg-white p-3 rounded-lg border border-slate-200">
                        <span className="text-primary block text-[10px] uppercase font-bold mb-1">
                          Détails de la commande (SING) :
                        </span>
                        <p className="text-slate-700 leading-relaxed font-medium">
                          {booking.specificDetails.mealSingDetails}
                        </p>
                      </div>
                    )}

                    {/* Traiteur Externe */}
                    {booking.specificDetails?.mealCatererType === 'EXTERNAL' && (
                      <div className="p-3 border border-amber-200 bg-amber-50 rounded-lg">
                        <span className="text-amber-700 block text-[10px] uppercase font-bold mb-1">
                          Traiteur Externe à contacter :
                        </span>
                        <p className="font-bold text-slate-800">{booking.specificDetails.mealExternalName}</p>
                        <p className="text-xs text-slate-600">
                          {booking.specificDetails.mealExternalPhone || 'Pas de téléphone'} •{' '}
                          {booking.specificDetails.mealExternalEmail || "Pas d'email"}
                        </p>
                      </div>
                    )}

                    {/* Notes Générales du client */}
                    {booking.customerNotes && (
                      <div className="pt-2 border-t border-slate-100">
                        <span className="text-slate-500 block text-[10px] uppercase font-bold mb-1">
                          Notes générales du client :
                        </span>
                        <p className="text-slate-700 font-medium whitespace-pre-wrap">{booking.customerNotes}</p>
                      </div>
                    )}

                    {/* Objectifs de l'événement */}
                    {booking.eventObjectives && (
                      <div className="pt-2 border-t border-slate-100">
                        <span className="text-slate-500 block text-[10px] uppercase font-bold mb-1">
                          Objectifs de l'événement :
                        </span>
                        <p className="text-slate-600 italic text-xs">{booking.eventObjectives}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4 border-t border-slate-100">
                  <div className="space-y-4">
                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Options demandées</h4>

                    <div
                      className={cn(
                        'flex items-center justify-between p-3.5 rounded-xl border transition-all',
                        editedDetails.wantsCoffeeBreak
                          ? 'bg-amber-50 border-amber-200'
                          : 'bg-slate-50 border-slate-100 opacity-75',
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={cn(
                            'flex items-center justify-center w-8 h-8 rounded-lg',
                            editedDetails.wantsCoffeeBreak
                              ? 'bg-amber-100 text-amber-600'
                              : 'bg-slate-200 text-slate-400',
                          )}
                        >
                          <Coffee className="w-4 h-4" />
                        </div>
                        <span
                          className={cn(
                            'text-sm font-semibold',
                            editedDetails.wantsCoffeeBreak ? 'text-amber-900' : 'text-slate-500',
                          )}
                        >
                          Pause café automatique
                        </span>
                      </div>
                      {editedDetails.wantsCoffeeBreak ? (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-200/50 px-2 py-1 rounded-md">
                          Demandée
                        </span>
                      ) : (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 italic">
                          Non demandée
                        </span>
                      )}
                    </div>

                    <div
                      className={cn(
                        'flex items-center justify-between p-3.5 rounded-xl border transition-all',
                        editedDetails.urgentQuote
                          ? 'bg-rose-50 border-rose-200'
                          : 'bg-slate-50 border-slate-100 opacity-75',
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={cn(
                            'flex items-center justify-center w-8 h-8 rounded-lg',
                            editedDetails.urgentQuote ? 'bg-rose-100 text-rose-600' : 'bg-slate-200 text-slate-400',
                          )}
                        >
                          <AlertCircle className="w-4 h-4" />
                        </div>
                        <span
                          className={cn(
                            'text-sm font-semibold',
                            editedDetails.urgentQuote ? 'text-rose-900' : 'text-slate-500',
                          )}
                        >
                          Traitement Urgent
                        </span>
                      </div>
                      {editedDetails.urgentQuote ? (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-rose-700 bg-rose-200/50 px-2 py-1 rounded-md">
                          Demandé
                        </span>
                      ) : (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 italic">
                          Non demandé
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                        Facturation Restauration
                      </h4>
                      {booking.status === 'PAID' ? (
                        <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider px-2 py-0.5 bg-slate-100 rounded-full">
                          Verrouillé
                        </span>
                      ) : (
                        <span className="text-[10px] uppercase font-bold text-primary tracking-wider px-2 py-0.5 bg-primary/10 rounded-full">
                          Édition
                        </span>
                      )}
                    </div>

                    {booking.status === 'PAID' && (
                      <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl">
                        <p className="text-xs text-slate-600 leading-relaxed font-medium">
                          ⚠️ Le devis a été marqué comme payé. La tarification restauration ne peut plus être modifiée.
                        </p>
                      </div>
                    )}

                    <div className="space-y-3">
                      <label className="text-sm font-semibold text-slate-700 block">
                        Prix unitaire (PU) Restauration
                      </label>
                      <div
                        className={`relative flex items-center overflow-hidden border rounded-xl transition-all ${
                          booking.status === 'PAID'
                            ? 'border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed'
                            : 'border-slate-200 focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary bg-white'
                        }`}
                      >
                        <div className="flex items-center justify-center w-14 bg-slate-50 border-r border-slate-200 self-stretch">
                          <span className="font-bold text-xs text-slate-500">XAF</span>
                        </div>
                        <input
                          type="number"
                          placeholder="Ex: 25000"
                          value={editedDetails.cateringManualPrice || ''}
                          onChange={(e) =>
                            handleUpdateDetail(
                              'cateringManualPrice',
                              e.target.value === '' ? undefined : Number(e.target.value),
                            )
                          }
                          disabled={booking.status === 'PAID'}
                          className="w-full px-4 py-3 outline-none placeholder:text-slate-300 font-bold text-slate-900 disabled:cursor-not-allowed disabled:bg-slate-50"
                        />
                      </div>
                    </div>

                    {/* Aperçu du calcul en temps réel */}
                    {editedDetails.cateringManualPrice > 0 && (
                      <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                        <p className="text-[10px] text-emerald-600 font-bold uppercase tracking-wider mb-1.5">
                          Calcul Restauration :
                        </p>
                        <p className="text-sm text-emerald-900 font-bold font-mono">
                          {(editedDetails.cateringManualPrice || 0).toLocaleString('fr-FR')} XAF
                          {' × '}
                          {editedDetails.daysCount || 1}j{' = '}
                          <span className="text-emerald-700 text-base">
                            {((editedDetails.cateringManualPrice || 0) * (editedDetails.daysCount || 1)).toLocaleString(
                              'fr-FR',
                            )}{' '}
                            XAF
                          </span>
                        </p>
                      </div>
                    )}

                    {booking.status !== 'PAID' && (
                      <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg">
                        <p className="text-xs text-blue-800 leading-relaxed font-medium">
                          Le prix unitaire sera uniquement multiplié par le nombre de jours de la réservation pour le
                          calcul du devis. Ce montant s'ajoutera au prix existant de la réservation.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>

        {/* Colonne Droite : Infos Client & Recap */}
        <div className="space-y-6">
          <section className="bg-slate-900 text-white rounded-2xl p-6 shadow-xl">
            <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-4">
              <User className="w-5 h-5 text-primary" />
              <h3 className="font-bold">Client</h3>
            </div>
            <div className="space-y-4">
              <div>
                <span className="text-white/50 text-xs font-semibold uppercase">Nom complet</span>
                <p className="font-medium">{booking.customerName}</p>
              </div>
              <div>
                <span className="text-white/50 text-xs font-semibold uppercase">Email</span>
                <p className="font-medium text-primary-light">{booking.customerEmail}</p>
              </div>
              {booking.companyName && (
                <div>
                  <span className="text-white/50 text-xs font-semibold uppercase">Entreprise</span>
                  <p className="font-medium">{booking.companyName}</p>
                </div>
              )}
            </div>
          </section>

          <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4 border-b border-slate-100 pb-4">
              <Calculator className="w-5 h-5 text-primary" />
              <h3 className="font-bold text-slate-800">Récapitulatif financier</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Prix réservation (fixe)</span>
                <span className="font-bold text-slate-900">{booking.totalAmount?.toLocaleString('fr-FR')} XAF</span>
              </div>

              {editedDetails.cateringManualPrice > 0 && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-emerald-600 font-medium">+ Restauration</span>
                    <span className="font-bold text-emerald-700">
                      +
                      {((editedDetails.cateringManualPrice || 0) * (editedDetails.daysCount || 1)).toLocaleString(
                        'fr-FR',
                      )}{' '}
                      XAF
                    </span>
                  </div>
                  <div className="border-t border-slate-200 pt-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-800 font-bold">Sous-total estimé (HT)</span>
                      <span className="font-bold text-primary text-base">
                        {(
                          (booking.totalAmount || 0) +
                          (editedDetails.cateringManualPrice || 0) * (editedDetails.daysCount || 1)
                        ).toLocaleString('fr-FR')}{' '}
                        XAF
                      </span>
                    </div>
                  </div>
                </>
              )}

              <div className="p-3 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                <p className="text-[10px] text-slate-400 font-medium uppercase text-center">
                  Taxes (TPS/CSS) appliquées automatiquement à l'enregistrement
                </p>
              </div>

              {/* Bouton Valider le Paiement */}
              {(booking?.status === 'QUOTED' ||
                booking?.status === 'QUOTE_REQUESTED' ||
                booking?.status === 'OVERDUE' ||
                booking?.status === 'URGENT') && (
                <button
                  onClick={() => setShowMarkAsPaidModal(true)}
                  disabled={saving}
                  className="w-full flex items-center justify-center gap-2 bg-emerald-600 text-white hover:bg-emerald-700 px-4 py-3 rounded-xl font-bold text-sm transition-all shadow-md shadow-emerald-600/20 disabled:opacity-75 disabled:cursor-not-allowed mt-4"
                  title="Valider le paiement et téléverser la preuve"
                >
                  <CheckCircle className="w-5 h-5" />
                  Valider le Paiement
                </button>
              )}

              {booking?.status === 'PAID' && (
                <div className="w-full px-4 py-3 bg-emerald-50 border-2 border-emerald-200 rounded-xl mt-4">
                  <div className="flex items-center justify-center gap-2 text-emerald-700">
                    <CheckCircle className="w-5 h-5" />
                    <span className="text-sm font-bold">Paiement Validé</span>
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      {/* Modale de prévisualisation */}
      <EmailPreviewModal
        open={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        onConfirmSend={async () => {
          // Appelle handleSave(true) qui naviguera automatiquement
          await handleSave(true);
          setShowPreviewModal(false);
        }}
        booking={booking}
      />

      {/* Modale de marquage comme payé */}
      <MarkAsPaidModal
        open={showMarkAsPaidModal}
        onClose={() => setShowMarkAsPaidModal(false)}
        onConfirm={handleMarkAsPaid}
        bookingReference={booking.reference}
        totalAmount={booking.totalAmount}
      />
    </div>
  );
}
